import { useEffect, useState } from 'react';

/**
 * A hook that provides a debounced value with immediate local state for responsive UI.
 *
 * @param externalValue - The external value to sync with (e.g., from URL params)
 * @param onDebouncedChange - Callback fired when the debounced value changes
 * @param delay - Debounce delay in milliseconds (default: 300)
 * @returns [localValue, setLocalValue] - The immediate local value and setter
 */
export function useDebouncedValue<T>(
  externalValue: T,
  onDebouncedChange: (value: T) => void,
  delay = 300,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState(externalValue);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (value !== externalValue) {
        onDebouncedChange(value);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [value, externalValue, onDebouncedChange, delay]);

  // Sync local state when external value changes
  useEffect(() => {
    setValue(externalValue);
  }, [externalValue]);

  return [value, setValue];
}
